'use client';

import { useState, useEffect, useCallback } from 'react';
import { adminDownloadsApi } from '../api';
import type { ApplicationDownload, AdminDownloadResponse } from '../types';

interface UseAdminDownloadsReturn {
  downloads: ApplicationDownload[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createDownload: (formData: FormData) => Promise<AdminDownloadResponse>;
  updateDownload: (id: string, formData: FormData) => Promise<AdminDownloadResponse>;
  deleteDownload: (id: string) => Promise<void>;
}

export function useAdminDownloads(): UseAdminDownloadsReturn {
  const [downloads, setDownloads] = useState<ApplicationDownload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDownloads = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminDownloadsApi.getDownloads();
      if (response.success && response.data) {
        setDownloads(response.data);
      } else {
        throw new Error('Failed to retrieve downloads list');
      }
    } catch (err: any) {
      setError(err instanceof Error ? err : new Error(err?.message || 'Failed to fetch downloads'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createDownload = async (formData: FormData): Promise<AdminDownloadResponse> => {
    try {
      const response = await adminDownloadsApi.createDownload(formData);
      if (response.success) {
        await fetchDownloads();
      }
      return response;
    } catch (err: any) {
      throw err instanceof Error ? err : new Error(err?.message || 'Failed to create download');
    }
  };

  const updateDownload = async (id: string, formData: FormData): Promise<AdminDownloadResponse> => {
    try {
      const response = await adminDownloadsApi.updateDownload(id, formData);
      if (response.success) {
        await fetchDownloads();
      }
      return response;
    } catch (err: any) {
      throw err instanceof Error ? err : new Error(err?.message || 'Failed to update download');
    }
  };

  const deleteDownload = async (id: string): Promise<void> => {
    try {
      const response = await adminDownloadsApi.deleteDownload(id);
      if (response.success) {
        await fetchDownloads();
      } else {
        throw new Error(response.message || 'Failed to delete download');
      }
    } catch (err: any) {
      throw err instanceof Error ? err : new Error(err?.message || 'Failed to delete download');
    }
  };

  useEffect(() => {
    fetchDownloads();
  }, [fetchDownloads]);

  return {
    downloads,
    isLoading,
    error,
    refetch: fetchDownloads,
    createDownload,
    updateDownload,
    deleteDownload
  };
}
